/* eslint-disable max-len */
import { MutableRefObject } from 'react';
import { Dispatch } from 'redux';
import { setAutoFillShown } from 'shared/helpers/auth';
import { setLoginAutoTriggered, showSignUp } from 'shared/store/app/actions';
import { getCookie } from 'helpers/utils/cookie';
import { isAutoFillShown } from 'shared/helpers/auth';
import { EXP_AB_AUTOFILL, userSessionTypes } from 'shared/constants';
import { isDeviceNotWeb } from 'shared/helpers/utils/device';
import { removeAuthFromStorage } from 'store/auth/helper';
import { fetchAuth } from 'shared/store/auth/action';
import { Device } from 'types';
import { hasUTMParams } from 'components/Header/helper';
import { pushEvent } from '@nykaa/nes-data-layer';
import {
  LOGIN,
  LOGINTYPE,
  LOGIN_SIGNUP_START,
  LOGIN_SUCCESS,
  SIGNUP_SUCCESS,
} from 'constants/auth';
import { logger } from '@nykaa/nes-logger';
import { loginPageName } from 'components/SignUp/helper';

interface AutoFillExpProps {
  scrollCount: MutableRefObject<number>;
  dispatch: Dispatch;
  configCount: number;
  handleOneTap: () => void;
  nudgeDelay: number;
  hideLoginBottomSheet?: boolean;
}

export const showAutoFillVariant = ({
  scrollCount,
  dispatch,
  configCount,
  handleOneTap,
  nudgeDelay,
  hideLoginBottomSheet = false,
}: AutoFillExpProps) => {
  const autoFillVar = getCookie(EXP_AB_AUTOFILL);
  const showLogin = hideLoginBottomSheet ? !hasUTMParams() : true;
  if (autoFillVar === 'B' && showLogin) {
    const timer = setTimeout(() => {
      if (!isAutoFillShown()) {
        const scrollUpdatedCount = scrollCount.current;
        scrollCount.current = scrollUpdatedCount + 1;
        if (scrollCount.current > configCount) {
          setAutoFillShown();
          showSignUp()(dispatch);
          setLoginAutoTriggered()(dispatch);
        }
      }
    }, nudgeDelay);
    return () => clearTimeout(timer);
  }
  if (autoFillVar === 'C') {
    const timer = setTimeout(() => {
      if (!isAutoFillShown()) {
        const scrollUpdatedCount = scrollCount.current;
        scrollCount.current = scrollUpdatedCount + 1;
        if (scrollCount.current > configCount) {
          setAutoFillShown();
          handleOneTap();
        }
      }
    }, nudgeDelay);
    return () => clearTimeout(timer);
  }
};

interface TrueCallerParams {
  requestId: string;
}
interface TrueCallerProps {
  fetchRequestId: () => () => Promise<string>;
  setGoogleOneTap: React.Dispatch<React.SetStateAction<boolean>>;
  trueCallerConfig: any;
  fetchTrueCallerStatus: (
    params: TrueCallerParams
  ) => (dispatch: Dispatch) => Promise<string | void>;
  isStatusFetching: boolean;
  isTrueCallerEnabled: boolean;
  dispatch: Dispatch;
  setToastMessage: React.Dispatch<React.SetStateAction<string>>;
  device: Device;
}

export const isFacebookOrInstaInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return (
    ua.includes('FBAN') ||
    ua.includes('FBAV') ||
    ua.includes('FB_IAB/FB4A') ||
    ua.includes('Instagram')
  );
};

export const fetchTrueCallerScript = ({
  fetchRequestId,
  setGoogleOneTap,
  trueCallerConfig,
  fetchTrueCallerStatus,
  isStatusFetching,
  isTrueCallerEnabled,
  dispatch,
  setToastMessage,
  device,
}: TrueCallerProps) => {
  if (
    !isTrueCallerEnabled ||
    isDeviceNotWeb(device) ||
    isFacebookOrInstaInAppBrowser()
  )
    return;

  const handleFetchTrueCallerStatusResponse =
    (timer: NodeJS.Timeout) => (data: any) => {
      const {
        state,
        useResponseSuccess,
        userStatus,
        errorCode,
        message,
        action,
      } = data || {};
      if (state !== 'processed') return;
      if (useResponseSuccess || userStatus === 'success') {
        const eventType = action === LOGIN ? LOGIN_SUCCESS : SIGNUP_SUCCESS;
        fetchAuth()(dispatch).then(() => {
          pushEvent(eventType, {
            loginType: LOGINTYPE.TRUECALLER,
            loginStatus: userSessionTypes.LOGGED_IN,
          });
        });
      } else if (!useResponseSuccess && errorCode === '757') {
        setToastMessage(message);
      }
      clearInterval(timer);
    };

  const handleTrueCallerResponse = (
    requestId: string,
    pollingDelay: number,
    pollingTimout: number
  ) => {
    const truecallerVisible = !document.hasFocus();
    window.datalayer.truecallerStatus = truecallerVisible;
    if (truecallerVisible) {
      window.datalayer.loginType = 'trueCaller';
      window.datalayer.event = 'userTruecallerTouched';
      let elapsedTime = 0;
      const timer = setInterval(() => {
        elapsedTime += pollingDelay;
        if (elapsedTime >= pollingTimout) {
          pushEvent('truecallerTimeout');
        }
        if (!isStatusFetching) {
          removeAuthFromStorage();
          fetchTrueCallerStatus({ requestId })(dispatch)
            .then(handleFetchTrueCallerStatusResponse(timer))
            .catch(() => clearInterval(timer));
        }
      }, pollingDelay * 1000);

      setTimeout(() => clearInterval(timer), pollingTimout * 1000);
    } else {
      window.datalayer.loginType = 'googleOneTap';
      setGoogleOneTap(true);
    }
  };

  const initiateTrueCallerLogin = async () => {
    const requestId = await fetchRequestId()();
    if (!requestId) return;
    if (!trueCallerConfig || Object.keys(trueCallerConfig).length === 0) {
      logger.error({}, 'Truecaller config not found');
      return;
    }

    const { TRUECALLER_API_KEY } = process.env;
    const {
      pollingTimout,
      pollingDelay,
      privacyUrl,
      tncUrl,
      callingTime,
      ctaColor,
      ctaTextColor,
    } = trueCallerConfig || {};

    const trueCallerUrl = `truecallersdk://truesdk/web_verify?type=btmsheet&requestNonce=${requestId}&partnerKey=${TRUECALLER_API_KEY}&partnerName=NykaaPreprod&lang=en&privacyUrl=${privacyUrl}&termsUrl=${tncUrl}&ctaPrefix=continuewith&ctaColor=%23${ctaColor}&ctaTextColor=%23${ctaTextColor}&btnShape=rect&loginPrefix=getstarted&loginSuffix=verifymobile&skipOption=useanothernum`;

    window.location.href = trueCallerUrl;
    sessionStorage.setItem('af_trc', 'true');
    pushEvent(LOGIN_SIGNUP_START, {
      loginType: LOGINTYPE.TRUECALLER,
      login_page: loginPageName(),
    });

    setTimeout(
      () => handleTrueCallerResponse(requestId, pollingDelay, pollingTimout),
      callingTime * 1000
    );
  };

  initiateTrueCallerLogin();
};
